import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';

import { AppSection, type Client, type Language, type Order, type Product } from './types';
import { localDateString } from './shared/domain';
import { countOrdersThisMonth, resolveEntitlement, type Entitlement } from './shared/entitlements';
import { PRO_LIMITS } from './shared/config';
import { RTL, translations } from './shared/i18n';
import { storage } from './lib/storage';
import { getWebDeviceId, webStore } from './services/security';

import Layout from './components/Layout';
import ActivationOverlay from './components/ActivationOverlay';
import Dashboard from './pages/Dashboard';
import ProductsPage from './pages/Products';
import ClientsPage from './pages/Clients';
import CommandPage from './pages/Command';
import HistoryPage from './pages/History';
import SettingsPage from './pages/Settings';

// Analytics pulls in Recharts (~400 kB). Loading it lazily keeps it out of the
// first paint, which on this app is the dashboard and the order screen.
const AnalyticsPage = lazy(() => import('./pages/Analytics'));

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState<AppSection>(AppSection.DASHBOARD);
  const [products, setProducts] = useState<Product[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [showActivation, setShowActivation] = useState(false);

  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('qd_lang');
    return saved === 'ar' || saved === 'en' || saved === 'fr' ? saved : 'fr';
  });

  // Data loads once. v1 re-read all three stores on every language change,
  // because the load effect listed `lang` as a dependency.
  useEffect(() => {
    setProducts(storage.getProducts());
    setClients(storage.getClients());
    setOrders(storage.getOrders());
  }, []);

  useEffect(() => {
    document.documentElement.dir = RTL[lang] ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    localStorage.setItem('qd_lang', lang);
  }, [lang]);

  const refreshEntitlement = useCallback(async () => {
    setEntitlement(await resolveEntitlement(webStore, await getWebDeviceId()));
  }, []);

  useEffect(() => { void refreshEntitlement(); }, [refreshEntitlement]);

  const t = translations[lang];
  const limits = entitlement?.limits ?? PRO_LIMITS;
  const ordersThisMonth = useMemo(
    () => countOrdersThisMonth(orders.map((o) => o.timestamp)),
    [orders],
  );

  const handleSaveProducts = (next: Product[]) => {
    setProducts(next);
    storage.saveProducts(next);
  };

  const handleSaveClients = (next: Client[]) => {
    setClients(next);
    storage.saveClients(next);
  };

  const handleSaveOrder = (order: Order) => {
    // Both branches go through the storage layer. v1 wrote the raw localStorage
    // key directly when editing, bypassing it entirely.
    const next = editingOrder
      ? orders.map((o) => (o.id === order.id ? order : o))
      : [order, ...orders];
    setOrders(next);
    storage.saveOrders(next);
    setEditingOrder(null);
    setActiveSection(AppSection.DASHBOARD);
  };

  const handleDeleteOrder = (id: string) => {
    const next = orders.filter((o) => o.id !== id);
    setOrders(next);
    storage.saveOrders(next);
  };

  const handleRestore = (p: Product[], c: Client[], o: Order[]) => {
    setProducts(p); setClients(c); setOrders(o);
    storage.saveProducts(p); storage.saveClients(c); storage.saveOrders(o);
  };

  const shared = { products, clients, orders, lang, t, limits };

  const renderSection = () => {
    switch (activeSection) {
      case AppSection.COMMAND:
        return (
          <CommandPage
            {...shared}
            editingOrder={editingOrder}
            ordersThisMonth={ordersThisMonth}
            onSaveOrder={handleSaveOrder}
            onCancel={() => { setEditingOrder(null); setActiveSection(AppSection.DASHBOARD); }}
          />
        );
      case AppSection.PRODUCTS:
        return <ProductsPage {...shared} onUpdate={handleSaveProducts} />;
      case AppSection.CLIENTS:
        return <ClientsPage {...shared} onUpdate={handleSaveClients} />;
      case AppSection.HISTORY:
        return (
          <HistoryPage
            {...shared}
            onEditOrder={(o) => { setEditingOrder(o); setActiveSection(AppSection.COMMAND); }}
            onDeleteOrder={handleDeleteOrder}
          />
        );
      case AppSection.ANALYTICS:
        return (
          <Suspense fallback={<div className="py-20 text-center text-sm font-bold text-slate-300">…</div>}>
            <AnalyticsPage {...shared} onUpgrade={() => setShowActivation(true)} />
          </Suspense>
        );
      case AppSection.SETTINGS:
        return (
          <SettingsPage
            {...shared}
            entitlement={entitlement}
            setLang={setLangState}
            onRestore={handleRestore}
            onActivate={() => setShowActivation(true)}
            onEntitlementChange={refreshEntitlement}
          />
        );
      default:
        return (
          <Dashboard
            {...shared}
            today={localDateString()}
            entitlement={entitlement}
            onStartOrder={() => setActiveSection(AppSection.COMMAND)}
            onUpgrade={() => setShowActivation(true)}
          />
        );
    }
  };

  return (
    <>
      <Analytics />
      <Layout
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        lang={lang}
        setLang={setLangState}
      >
        {renderSection()}
      </Layout>

      {showActivation && (
        <ActivationOverlay
          lang={lang}
          onClose={() => setShowActivation(false)}
          onActivated={async () => { await refreshEntitlement(); setShowActivation(false); }}
        />
      )}
    </>
  );
};

export default App;
